import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import IconButton from "../../../components/button/IconButton";
import Input from "../../../components/form-item/Input";
import Select, { SelectOption } from "../../../components/form-item/Select";
import Table, { TableColumn } from "../../../components/table/Table";
import TitleBlock from "../../../components/block/TitleBlock";
import HelpHint from "../../../components/help-hint/HelpHint";
import ToolTip from "../../../components/tooltip/ToolTip";
import {
  SortableDragHandle,
  SortableItem,
  SortableList,
} from "../../../components/sortable";
import {
  useGetProfileContentQuery,
  useUpdateProfileContentMutation,
} from "../../../app/services/creatorProfile";
import type {
  ProfileFooter,
  ProfileFooterMenu,
  ProfileFooterSocial,
} from "../../../types/profile";

// =====================================================================
// 内部モデル
// =====================================================================

/**
 * フッターの 1 行（FE 内部表現）。
 *
 * BE は `menus[]`（メイン/サブ）と `socials[]`（アイコン）の 2 配列だが、編集 UI では
 * 1 つの表で全行を管理するため、3 種をまとめた 1 配列で扱う。保存時に
 * `toFooterPayload` で 2 配列へ分解して PUT する。
 */
type FooterRowKind = "main" | "icon" | "sub";

interface FooterRow {
  /** FE 内部の一意キー。BE 側 id は全置換 PUT で常に変わるため使わない。 */
  key: string;
  kind: FooterRowKind;
  /** main/sub のみ使用。icon では空文字。 */
  title_jp: string;
  title_en: string;
  url: string;
}

const KIND_OPTIONS: SelectOption[] = [
  { value: "main", name: "メインメニュー" },
  { value: "icon", name: "アイコンメニュー" },
  { value: "sub", name: "サブメニュー" },
];

/** 表の必須の並び順。行はこの順で種別ごとにまとまって表示する。 */
const KIND_ORDER: FooterRowKind[] = ["main", "icon", "sub"];

const kindRank = (kind: FooterRowKind): number => KIND_ORDER.indexOf(kind);

/**
 * 種別ごとのまとまり（メイン → アイコン → サブ）を強制する。
 * 安定ソートのため、同一種別内の相対順序は保たれる。D&D で他セクションへ
 * ドロップされた行も、自分のセクションの端へクランプされる。
 */
const groupByKind = (rows: FooterRow[]): FooterRow[] =>
  [...rows].sort((a, b) => kindRank(a.kind) - kindRank(b.kind));

/**
 * 指定 `kind` のセクション末尾に挿入する index を返す。
 * 該当 kind がまだ無ければ、`KIND_ORDER` で次に来るセクションの先頭を返す。
 */
const findInsertIndexForKind = (
  rows: FooterRow[],
  kind: FooterRowKind,
): number => {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].kind === kind) return i + 1;
  }
  for (let i = 0; i < rows.length; i++) {
    if (kindRank(rows[i].kind) > kindRank(kind)) return i;
  }
  return rows.length;
};

// 公開ページと揃える: src/pages/profile/components/ProfileFooterSection.tsx
const SOCIAL_ICON: Record<string, string> = {
  home: "public",
  twitter: "alternate_email",
  fb: "group",
  insta: "photo_camera",
  youtube: "smart_display",
  tiktok: "music_note",
  note: "edit_note",
};

/**
 * URL のホスト名からアイコン種別（`type_social_link`）を自動判定する。
 * 判定できない・不正な URL は「公式サイト」扱い（home）。
 */
const detectSocialType = (url: string): string => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    if (host === "x.com" || host.endsWith("twitter.com")) return "twitter";
    if (host === "fb.com" || host.endsWith("facebook.com")) return "fb";
    if (host.endsWith("instagram.com")) return "insta";
    if (host === "youtu.be" || host.endsWith("youtube.com")) return "youtube";
    if (host.endsWith("tiktok.com")) return "tiktok";
    if (host.endsWith("note.com")) return "note";
  } catch {
    // 不正 URL は home 扱い
  }
  return "home";
};

const newKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `footer-${Date.now()}-${Math.random()}`;

// =====================================================================
// BE ↔ FE 変換
// =====================================================================

const fromFooter = (
  footer: ProfileFooter | null,
): { rows: FooterRow[]; copyright: string } => {
  if (!footer) return { rows: [], copyright: "" };
  const menus = [...(footer.menus ?? [])];
  const socials = [...(footer.socials ?? [])];

  const mains = menus
    .filter((m) => m.type_footer === "1")
    .sort((a, b) => a.order - b.order)
    .map(
      (m): FooterRow => ({
        key: newKey(),
        kind: "main",
        title_jp: m.title_jp ?? "",
        title_en: m.title_en ?? "",
        url: m.url ?? "",
      }),
    );

  const icons = socials
    .sort((a, b) => a.order - b.order)
    .map(
      (s): FooterRow => ({
        key: newKey(),
        kind: "icon",
        title_jp: "",
        title_en: "",
        url: s.url ?? "",
      }),
    );

  const subs = menus
    .filter((m) => m.type_footer === "2")
    .sort((a, b) => a.order - b.order)
    .map(
      (m): FooterRow => ({
        key: newKey(),
        kind: "sub",
        title_jp: m.title_jp ?? "",
        title_en: m.title_en ?? "",
        url: m.url ?? "",
      }),
    );

  return {
    rows: [...mains, ...icons, ...subs],
    copyright: footer.copyright ?? "",
  };
};

const toFooterPayload = (
  rows: FooterRow[],
  copyright: string,
): ProfileFooter => {
  const menus: ProfileFooterMenu[] = [];
  const socials: ProfileFooterSocial[] = [];
  const orders: Record<FooterRowKind, number> = { main: 0, icon: 0, sub: 0 };
  for (const r of rows) {
    if (r.kind === "icon") {
      socials.push({
        id: 0,
        type_social_link: detectSocialType(r.url),
        url: r.url,
        order: orders.icon++,
      });
    } else {
      menus.push({
        id: 0,
        type_footer: r.kind === "sub" ? "2" : "1",
        title_jp: r.title_jp,
        title_en: r.title_en,
        url: r.url,
        order: r.kind === "sub" ? orders.sub++ : orders.main++,
      });
    }
  }
  return { copyright, menus, socials };
};

// =====================================================================
// スタイル
// =====================================================================

/** テーブルセル内に置く inline 入力フィールド共通スタイル。`MySiteWorks` と同じ密度。 */
const INLINE_INPUT_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "1px 6px",
  fontSize: 13,
  lineHeight: "150%",
  height: 24,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

/** 末尾の行追加入力のスタイル。ショットリストの「行追加」入力と同じ密度。 */
const ADD_INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  padding: "2px 8px",
  fontSize: 13,
  lineHeight: "150%",
  height: 26,
  border: "none",
  outline: "none",
  boxShadow: "none",
  background: "transparent",
};

// =====================================================================
// テーブル列定義
// =====================================================================

// 右カラムは全幅（MyProfile の wideRightColumn 対象）。固定幅は操作列のみとし、
// URL / リンク名は minWidth を確保しつつ画面幅に応じて広がる。
// 狭い画面では Table 内蔵のスクロール領域（横スクロール + ホイールパン）に任せる。
const COLUMNS: TableColumn[] = [
  { key: "drag", width: 36, minWidth: 36, resizable: false },
  { key: "url", header: "URL", minWidth: 180 },
  { key: "kind", header: "種別", width: 140, minWidth: 100 },
  { key: "title_jp", header: "リンク名 (JP)", minWidth: 110 },
  { key: "title_en", header: "リンク名 (EN)", minWidth: 110 },
  { key: "delete", header: "", width: 36, minWidth: 36, resizable: false },
];

// =====================================================================
// 本体
// =====================================================================

/**
 * マイサイト「フッターのリンク設定」。
 *
 * - メインメニュー / アイコンメニュー / サブメニューを **1 つの表** で管理
 * - 行の「種別」はセレクトで切り替え（アイコンメニューはリンク名がグレーアウト）
 * - 最下部の追加行（ショットリストと同型）に URL を入力して Enter で行を追加
 * - アイコンの種類は URL から自動判定する（X / Instagram / YouTube など）
 * - 行は常に メイン → アイコン → サブ の順に種別ごとにまとまって並ぶ
 * - D&D の並び替えは同一種別のセクション内のみ。フッターの表示順は表の上からの順
 * - 各セルは **インライン編集**。フォーカスを外すと全置換 PUT で永続化する
 * - 永続化失敗時はローカル state をロールバックして toast 表示
 */
const MySiteFooter: React.FC = () => {
  const { data: contentResp } = useGetProfileContentQuery();
  const [updateContent] = useUpdateProfileContentMutation();

  const [rows, setRows] = useState<FooterRow[]>([]);
  const [copyright, setCopyright] = useState<string>("");
  /** 最下部の追加行に入力中の URL。Enter で行として確定する。 */
  const [newUrl, setNewUrl] = useState<string>("");

  useEffect(() => {
    const { rows: nextRows, copyright: nextCopyright } = fromFooter(
      contentResp?.data?.footer ?? null,
    );
    setRows(nextRows);
    setCopyright(nextCopyright);
  }, [contentResp]);

  const persist = useCallback(
    async (nextRows: FooterRow[], nextCopyright: string) => {
      const previousRows = rows;
      const previousCopyright = copyright;
      setRows(nextRows);
      setCopyright(nextCopyright);
      try {
        await updateContent({
          footer: toFooterPayload(nextRows, nextCopyright),
        }).unwrap();
      } catch {
        toast.error("フッター設定の保存に失敗しました");
        setRows(previousRows);
        setCopyright(previousCopyright);
      }
    },
    [rows, copyright, updateContent],
  );

  // ===== 編集ハンドラ =====

  /** 入力中: state のみ更新（保存しない）。 */
  const patchRow = (key: string, patch: Partial<FooterRow>) =>
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );

  /** 入力 onBlur: 現在の全 rows + copyright を PUT。 */
  const commitAll = () => {
    persist(rows, copyright);
  };

  /** 種別変更: 移動先セクションの末尾に挿入して即保存。icon はリンク名をクリア。 */
  const changeKind = (key: string, newKind: FooterRowKind) => {
    const target = rows.find((r) => r.key === key);
    if (!target || target.kind === newKind) return;
    const others = rows.filter((r) => r.key !== key);
    const moved: FooterRow =
      newKind === "icon"
        ? { ...target, kind: newKind, title_jp: "", title_en: "" }
        : { ...target, kind: newKind };
    const at = findInsertIndexForKind(others, newKind);
    persist([...others.slice(0, at), moved, ...others.slice(at)], copyright);
  };

  const handleDelete = (key: string) => {
    persist(
      rows.filter((r) => r.key !== key),
      copyright,
    );
  };

  /** 追加行: URL を入力して Enter で新しい行（メインメニュー）をセクション末尾に追加する。 */
  const handleAddRow = () => {
    const url = newUrl.trim();
    if (!url) return;
    setNewUrl("");
    const newRow: FooterRow = {
      key: newKey(),
      kind: "main",
      title_jp: "",
      title_en: "",
      url,
    };
    const at = findInsertIndexForKind(rows, "main");
    persist([...rows.slice(0, at), newRow, ...rows.slice(at)], copyright);
  };

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleAddRow();
    }
  };

  /**
   * D&D 並び替え: 同一種別のセクション内でのみ有効。
   * 他セクションへドロップした場合は `groupByKind` の安定ソートにより
   * 自分のセクションの端（上下いずれか近い方）へクランプされる。
   */
  const handleReorder = async (newIds: string[]) => {
    const idToRow = new Map(rows.map((r) => [r.key, r]));
    const next = groupByKind(
      newIds.map((id) => idToRow.get(id)).filter((r): r is FooterRow => !!r),
    );
    if (next.every((r, i) => r.key === rows[i]?.key)) return;
    await persist(next, copyright);
  };

  // ===== 行レンダリング =====

  /** URL セル: アイコン行は自動判定したアイコンをプレビュー表示する。 */
  const renderUrlCell = (row: FooterRow) => {
    const input = (
      <input
        type="url"
        value={row.url}
        placeholder={
          row.kind === "icon"
            ? "https://x.com/yourname"
            : "https://example.com/"
        }
        onChange={(e) => patchRow(row.key, { url: e.target.value })}
        onBlur={commitAll}
        style={INLINE_INPUT_STYLE}
      />
    );
    return (
      <td>
        {row.kind === "icon" ? (
          <div className="u-row" style={{ alignItems: "center" }}>
            <ToolTip description="URL から自動判定されるアイコン">
              <span
                className="material-symbols-rounded u-text-light-gray"
                style={{ fontSize: 16, flexShrink: 0, paddingLeft: 6 }}
                aria-hidden
              >
                {SOCIAL_ICON[detectSocialType(row.url)]}
              </span>
            </ToolTip>
            {input}
          </div>
        ) : (
          input
        )}
      </td>
    );
  };

  /** リンク名セル: アイコン行はグレーアウトして入力不可にする。 */
  const renderTitleCell = (row: FooterRow, field: "title_jp" | "title_en") => {
    if (row.kind === "icon") {
      return (
        <td className="table-cell-dimmed">
          <input type="text" value="" disabled style={INLINE_INPUT_STYLE} />
        </td>
      );
    }
    return (
      <td>
        <input
          type="text"
          value={row[field]}
          placeholder={field === "title_jp" ? "例: 会社概要" : "e.g. About"}
          onChange={(e) => patchRow(row.key, { [field]: e.target.value })}
          onBlur={commitAll}
          style={INLINE_INPUT_STYLE}
        />
      </td>
    );
  };

  const renderRow = (row: FooterRow) => (
    <SortableItem id={row.key} key={row.key}>
      {({ setNodeRef, style, handleProps }) => (
        <tr ref={setNodeRef} style={style}>
          <td style={{ textAlign: "center" }}>
            <SortableDragHandle {...handleProps} />
          </td>
          {renderUrlCell(row)}
          <td>
            <Select
              size="small"
              value={row.kind}
              options={KIND_OPTIONS}
              onChange={(e) =>
                changeKind(row.key, e.target.value as FooterRowKind)
              }
            />
          </td>
          {renderTitleCell(row, "title_jp")}
          {renderTitleCell(row, "title_en")}
          <td style={{ textAlign: "center" }}>
            <IconButton
              icon="delete"
              size={18}
              onClick={() => handleDelete(row.key)}
              ariaLabel="削除"
              title="削除"
            />
          </td>
        </tr>
      )}
    </SortableItem>
  );

  /** 最下部の行追加行。ショットリストの「行追加」行と同型の 1 セル入力。 */
  const renderAddRow = () => (
    <tr>
      <td colSpan={COLUMNS.length}>
        <div className="u-row u-items-center u-gap8">
          <span
            className="material-symbols-rounded"
            style={{ fontSize: 16, color: "var(--soremo-light-gray)" }}
          >
            add
          </span>
          <input
            type="url"
            placeholder="URL を入力して Enter で行を追加"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={handleAddKeyDown}
            style={ADD_INPUT_STYLE}
          />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="u-col u-gap24">
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="フッターのリンク設定" />
        <HelpHint description={"フッターに表示するメニュー・アイコンを 1 つの表で管理します。最下部の行に URL を入力して Enter を押すと行が追加され、「種別」で表示先を切り替えられます。アイコンメニューのアイコンは URL から自動判定されます。"} />
      </div>

      <SortableList
        items={rows}
        getId={(r) => r.key}
        strategy="vertical"
        axis="y"
        onReorder={handleReorder}
      >
        <Table columns={COLUMNS} minWidth={640}>
          {rows.map((r) => renderRow(r))}
          {renderAddRow()}
        </Table>
      </SortableList>

      {/* コピーライト */}
      <Input
        label="コピーライト"
        option="optional"
        type="text"
        value={copyright}
        onChange={(e) => setCopyright(e.target.value)}
        onBlur={commitAll}
        placeholder="©SOREMO Co., ltd. All right reserved."
        width="100%"
        name="copyright"
      />
    </div>
  );
};

export default MySiteFooter;
